import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { collection, query, where, getDocs, doc, getDoc, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db, isSchoolAdmin, isSuperAdmin } from '../../../utils/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { Teacher, User } from '../../../utils/firebase';
import { Timestamp } from 'firebase/firestore';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import Dialog from '../../../components/ui/Dialog';
import QrCodeIcon from '../../../components/icons/QrCodeIcon';

// 타입 가드 함수
const isTeacher = (user: User): user is Teacher => {
  return user.role === 'teacher' || user.role === 'superAdmin' || user.role === 'schoolAdmin';
};

interface ScannedItem {
  itemId: string;
  studentId: string;
  name: string;
  timestamp: number;
}

interface StudentInfo {
  id: string;
  name: string;
  class: number;
  grade: number;
  number: number;
}

interface ItemInfo {
  id: string;
  name: string;
  description: string;
  itemCategory: string;
}

interface StudentItem {
  itemId: string;
  quantity: number;
  purchasedAt: Timestamp | null;
  lastUsedAt: Timestamp | null;
}

interface Student {
  id: string;
  name: string;
  email: string;
  role: string;
  points: number;
  items: {
    [key: string]: StudentItem;
  };
  displayName?: string;
  class?: number;
  grade?: number;
  number?: number;
}

const ItemScanner: React.FC = () => {
  const { currentUser } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [scannedItem, setScannedItem] = useState<ScannedItem | null>(null);
  const [studentInfo, setStudentInfo] = useState<StudentInfo | null>(null);
  const [itemInfo, setItemInfo] = useState<ItemInfo | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scanner, setScanner] = useState<Html5Qrcode | null>(null);
  const [selectedCamera, setSelectedCamera] = useState<string | null>(null);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // 모바일 기기 감지
    const mobileCheck = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    setIsMobile(mobileCheck);
  }, []);

  useEffect(() => {
    // 사용 가능한 카메라 목록 가져오기
    const getCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setCameras(videoDevices);
        
        if (videoDevices.length > 0) {
          if (isMobile) {
            // 모바일에서는 후면 카메라 찾기
            const backCamera = videoDevices.find(device => 
              device.label.toLowerCase().includes('back') || 
              device.label.toLowerCase().includes('후면') ||
              device.label.toLowerCase().includes('environment')
            );
            setSelectedCamera(backCamera?.deviceId || videoDevices[0].deviceId);
          } else {
            setSelectedCamera(videoDevices[0].deviceId);
          }
        }
      } catch (err) {
        console.error('카메라 목록을 가져오는데 실패했습니다:', err);
        setError('카메라 접근이 거부되었습니다. 브라우저 설정에서 카메라 권한을 허용해주세요.');
      }
    };
    getCameras();
  }, [isMobile]);

  useEffect(() => {
    if (scanning && selectedCamera) {
      const html5QrCode = new Html5Qrcode("qr-reader");
      setScanner(html5QrCode);
      
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
        videoConstraints: {
          deviceId: selectedCamera,
          facingMode: isMobile ? "environment" : "user",
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };

      console.log('카메라 시작 시도:', { deviceId: selectedCamera, isMobile, config });

      html5QrCode.start(
        { deviceId: selectedCamera },
        config,
        (decodedText) => {
          handleScan(decodedText);
        },
        (errorMessage) => {
          if (!errorMessage.includes('NotFoundError')) {
            console.warn('QR 스캔 오류:', errorMessage);
          }
        }
      ).catch((err) => {
        console.error('카메라 초기화 오류:', err);
        if (err instanceof DOMException) {
          switch (err.name) {
            case 'NotAllowedError':
              setError('카메라 접근이 거부되었습니다. 브라우저 설정에서 카메라 권한을 허용해주세요.');
              break;
            case 'NotFoundError':
              setError('카메라를 찾을 수 없습니다. 카메라가 연결되어 있는지 확인해주세요.');
              break;
            case 'NotReadableError':
              setError('카메라에 접근할 수 없습니다. 다른 앱이 카메라를 사용 중인지 확인해주세요.');
              break;
            default:
              setError('카메라 초기화 중 오류가 발생했습니다. 페이지를 새로고침해주세요.');
          }
        } else {
          setError('카메라 초기화 중 오류가 발생했습니다. 페이지를 새로고침해주세요.');
        }
        setScanning(false);
      });

      return () => {
        if (html5QrCode) {
          try {
            html5QrCode.stop().catch(() => {});
            html5QrCode.clear();
          } catch (err) {
            console.warn('스캐너 정리 중 경고:', err);
          }
        }
      };
    }
  }, [scanning, selectedCamera]);

  const handleCameraChange = (deviceId: string) => {
    setSelectedCamera(deviceId);
    if (scanning) {
      setScanning(false);
      setTimeout(() => setScanning(true), 100);
    }
  };

  const handleScan = async (data: string) => {
    try {
      // 스캐너 완전히 중지
      if (scanner && scanning) {
        try {
          await scanner.stop().catch(err => {
            console.warn('스캐너 중지 중 경고:', err);
          });
          setScanning(false);
        } catch (err) {
          console.warn('스캐너 중지 중 오류:', err);
        }
      }

      // QR 코드 데이터 파싱
      let parsedData: ScannedItem;
      try {
        parsedData = JSON.parse(data) as ScannedItem;
        console.log('파싱된 QR 코드 데이터:', parsedData);
      } catch (parseError) {
        setError('QR 코드 형식이 올바르지 않습니다.');
        return;
      }

      // 필수 필드 검증
      if (!parsedData.itemId || !parsedData.studentId) {
        setError('QR 코드에 필요한 정보가 누락되었습니다.');
        return;
      }

      if (!currentUser?.schoolId || !currentUser?.classId) {
        setError('학교 또는 반 정보가 없습니다.');
        return;
      }

      // 교사 권한 확인
      if (!isTeacher(currentUser)) {
        setError('아이템 사용 승인 권한이 없습니다.');
        return;
      }

      setScannedItem(parsedData);
      setResult(data);
      
      // 학생 정보 가져오기
      const studentRef = doc(db, 'schools', currentUser.schoolId, 'classes', currentUser.classId, 'students', parsedData.studentId);
      console.log('학생 정보 조회 경로:', studentRef.path);
      const studentDoc = await getDoc(studentRef);
      
      if (!studentDoc.exists()) {
        console.error('학생 정보를 찾을 수 없습니다. ID:', parsedData.studentId);
        setError('학생 정보를 찾을 수 없습니다. 학생 ID를 확인해주세요.');
        return;
      }

      const studentData = studentDoc.data() as Student;
      console.log('찾은 학생 정보:', studentData);
      
      // 사용자 정보에서 학생 정보 추출
      const studentInfo = {
        id: studentDoc.id,
        name: studentData.name || studentData.displayName || '이름 없음',
        class: studentData.class ?? 0,
        grade: studentData.grade ?? 0,
        number: studentData.number ?? 0
      };
      
      setStudentInfo(studentInfo);
      
      // 아이템 정보 가져오기 - 학급 아이템과 교과 아이템 구분
      let itemDoc = null;
      
      // 1. 학급 아이템 시도
      const classItemRef = doc(db, 'schools', currentUser.schoolId, 'classes', currentUser.classId, 'storeItems', parsedData.itemId);
      console.log('학급 아이템 조회 경로:', classItemRef.path);
      itemDoc = await getDoc(classItemRef);
      
      // 2. 교과 아이템 시도
      if (!itemDoc.exists()) {
        const subjectItemRef = doc(db, 'schools', currentUser.schoolId, 'storeItems', parsedData.itemId);
        console.log('교과 아이템 조회 경로:', subjectItemRef.path);
        itemDoc = await getDoc(subjectItemRef);
      }
      
      if (!itemDoc.exists()) {
        console.error('아이템 정보를 찾을 수 없습니다. ID:', parsedData.itemId);
        setError('아이템 정보를 찾을 수 없습니다.');
        return;
      }

      const itemData = itemDoc.data();
      console.log('찾은 아이템 정보:', itemData);

      // 아이템 사용 가능 여부 확인
      if (!itemData.isActive) {
        setError('비활성화된 아이템입니다.');
        return;
      }

      if (itemData.isLimited && !itemData.isUnlimitedStock && itemData.stock <= 0) {
        setError('재고가 없는 아이템입니다.');
        return;
      }

      // 판매 기간 확인
      if (itemData.salePeriod) {
        const now = new Date();
        const start = itemData.salePeriod.start.toDate();
        const end = itemData.salePeriod.end.toDate();
        
        if (now < start || now > end) {
          setError('아이템 사용 가능 기간이 아닙니다.');
          return;
        }
      }

      setItemInfo({
        id: itemDoc.id,
        name: itemData.name,
        description: itemData.description,
        itemCategory: itemData.itemCategory
      });

      // 사용 확인 다이얼로그 표시
      setConfirmDialogOpen(true);

      setSuccess('아이템 사용이 확인되었습니다.');
      setConfirmDialogOpen(false);
      resetScanner();
    } catch (err) {
      console.error('QR 코드 처리 중 오류 발생:', err);
      setError('QR 코드 처리 중 오류가 발생했습니다.');
    }
  };

  const handleError = (err: string) => {
    // QR 코드가 감지되지 않은 경우는 일반적인 상황이므로 에러로 표시하지 않음
    if (err.includes('No barcode or QR code detected') || err.includes('No MultiFormat Readers were able to detect the code')) {
      return;
    }
    
    console.error('Error scanning QR code:', err);
    setError('QR 코드 스캔 중 오류가 발생했습니다.');
    setScanning(false);
  };

  const handleConfirmUseItem = async () => {
    if (!scannedItem || !currentUser || !currentUser.schoolId || !currentUser.classId) return;
    
    try {
      setLoading(true);
      
      // 스캐너 중지
      setScanning(false);
      if (scanner) {
        try {
          await scanner.stop().catch(() => {});
          scanner.clear();
        } catch (err) {
          console.warn('스캐너 정리 중 경고:', err);
        }
      }
      
      // 학생의 아이템 개수 감소
      const studentRef = doc(db, 'schools', currentUser.schoolId, 'classes', currentUser.classId, 'students', scannedItem.studentId);
      const studentDoc = await getDoc(studentRef);
      
      if (!studentDoc.exists()) {
        throw new Error('학생 정보를 찾을 수 없습니다.');
      }
      
      const studentData = studentDoc.data() as Student;
      const studentItems = studentData.items || {};
      const itemInfo = studentItems[scannedItem.itemId];
      
      if (!itemInfo || itemInfo.quantity <= 0) {
        throw new Error('보유한 아이템이 없습니다.');
      }
      
      // 아이템 개수 감소 및 마지막 사용 시간 업데이트
      await updateDoc(studentRef, {
        [`items.${scannedItem.itemId}.quantity`]: itemInfo.quantity - 1,
        [`items.${scannedItem.itemId}.lastUsedAt`]: serverTimestamp()
      });
      
      // 아이템 사용 기록 생성
      const usagesCollection = collection(db, 'schools', currentUser.schoolId, 'classes', currentUser.classId, 'itemUsages');
      const usageRef = doc(usagesCollection);
      
      await setDoc(usageRef, {
        studentId: scannedItem.studentId,
        schoolId: currentUser.schoolId,
        classId: currentUser.classId,
        itemId: scannedItem.itemId,
        itemName: scannedItem.name,
        usedAt: serverTimestamp(),
        status: 'completed',
        confirmedBy: currentUser.uid
      });
      
      setSuccess('아이템 사용이 확인되었습니다.');
      setConfirmDialogOpen(false);
      resetScanner();
    } catch (err) {
      console.error('아이템 사용 확인 중 오류:', err);
      setError(err instanceof Error ? err.message : '아이템 사용 확인 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const resetScanner = () => {
    setScanning(false);
    if (scanner) {
      try {
        scanner.stop().catch(() => {});
        scanner.clear();
      } catch (err) {
        console.warn('스캐너 정리 중 경고:', err);
      }
    }
    setResult(null);
    setScannedItem(null);
    setStudentInfo(null);
    setItemInfo(null);
    setError(null);
  };

  const startScanning = async () => {
    try {
      // 카메라 권한 요청
      await navigator.mediaDevices.getUserMedia({ video: true });
      resetScanner();
      setScanning(true);
    } catch (err) {
      console.error('카메라 권한 요청 실패:', err);
      setError('카메라 접근이 거부되었습니다. 브라우저 설정에서 카메라 권한을 허용해주세요.');
    }
  };

  const hasTeacherAccess = currentUser && (isTeacher(currentUser) || isSchoolAdmin(currentUser) || isSuperAdmin(currentUser));
  if (!hasTeacherAccess) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ padding: 16, background: '#ffebee', color: '#c62828', borderRadius: 8 }}>교사 권한이 필요합니다.</div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {error && (
        <div style={{ marginBottom: 16, padding: 12, background: '#ffebee', color: '#c62828', borderRadius: 8 }}>{error}</div>
      )}
      {success && (
        <div style={{ marginBottom: 16, padding: 12, background: '#e8f5e9', color: '#2e7d32', borderRadius: 8 }}>{success}</div>
      )}
      <h1 style={{ marginBottom: 24, fontSize: 24 }}>QR 코드 스캐너</h1>
      
      {!isMobile && cameras.length > 1 && (
        <Card padding="16px" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {cameras.map((camera) => (
              <Button
                key={camera.deviceId}
                variant={selectedCamera === camera.deviceId ? 'primary' : 'outline'}
                size="small"
                onClick={() => handleCameraChange(camera.deviceId)}
              >
                {camera.label || `카메라 ${cameras.indexOf(camera) + 1}`}
              </Button>
            ))}
          </div>
        </Card>
      )}
      
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <div id="qr-reader" style={{ width: '100%', maxWidth: 600, margin: '0 auto', position: 'relative', marginBottom: 32 }}>
          {!scanning && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
              <QrCodeIcon width={60} height={60} style={{ marginBottom: 8, color: '#666' }} />
              <p style={{ margin: 0, color: '#666', fontSize: 16 }}>스캔을 시작하려면 아래 버튼을 클릭하세요</p>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 32 }}>
          <Button variant="primary" onClick={startScanning} disabled={scanning} leftIcon={<QrCodeIcon width={20} height={20} />}>
            스캔 시작
          </Button>
          {scanning && (
            <Button variant="outline" onClick={() => setScanning(false)}>
              스캔 중지
            </Button>
          )}
        </div>
      </div>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        title="아이템 사용 확인"
        actions={
          <>
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>취소</Button>
            <Button variant="primary" onClick={handleConfirmUseItem} disabled={loading}>
              {loading ? '처리 중...' : '확인'}
            </Button>
          </>
        }
      >
        {studentInfo && (
          <Card padding="16px" style={{ marginBottom: 16 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>학생 정보</h3>
            <p style={{ margin: 4 }}>이름: {studentInfo.name}</p>
            <p style={{ margin: 4 }}>학년: {studentInfo.grade}학년</p>
            <p style={{ margin: 4 }}>반: {studentInfo.class}반</p>
            <p style={{ margin: 4 }}>번호: {studentInfo.number}번</p>
          </Card>
        )}
        {itemInfo && (
          <Card padding="16px">
            <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>아이템 정보</h3>
            <p style={{ margin: 4 }}>이름: {itemInfo.name}</p>
            <p style={{ margin: 4 }}>설명: {itemInfo.description}</p>
            <p style={{ margin: 4 }}>카테고리: {itemInfo.itemCategory}</p>
          </Card>
        )}
      </Dialog>
    </div>
  );
};

export default ItemScanner; 